import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1763328744724-8140be40802c?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function OverOns() {
  usePageMeta({
    title: "Over ons, LevelLeven",
    description:
      "Waarom LevelLeven bestaat, hoe het is ontstaan en wat het grote doel is: mensen weer in beweging brengen vóór re-integratie.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 reveal">
              <SectionLabel>Over ons</SectionLabel>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1F5132] font-light">
                Wij bouwen de fase{" "}
                <span className="italic text-[#E06B3F]">die het systeem overslaat</span>.
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-[#1F5132] leading-relaxed font-light max-w-xl">
                LevelLeven is niet nóg een re-integratiebureau. Wij zijn de
                ontbrekende schakel tussen stilstand en beweging, voor
                inwoners die al jaren wachten op iets wat hen écht raakt.
              </p>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="rounded-3xl overflow-hidden aspect-[5/4] bg-[#F0EAD8]">
                <img
                  src={HERO_IMG}
                  alt="Drie mensen in gesprek buiten"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WAAROM LEVELLEVEN */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4 reveal">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-6">
                01 · Waarom LevelLeven
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                Omdat stilstand{" "}
                <span className="italic text-[#E06B3F]">een kostenpost is</span>,
                voor mensen én voor gemeenten.
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 reveal space-y-6 text-lg text-[#1F5132] font-light leading-relaxed">
              <p>
                Duizenden mensen staan al jarenlang ingeschreven zonder
                beweging. Niet omdat ze niet willen, maar omdat het systeem
                geen ruimte biedt voor wat écht nodig is: veiligheid,
                vertrouwen en tijd om weer in zichzelf te geloven.
              </p>
              <p>
                Traditionele trajecten starten waar mensen nog niet zijn.
                LevelLeven bouwt het fundament daarvóór, zodat wat volgt
                eindelijk stand houdt.
              </p>
              <p className="text-[#1F5132]">
                Wij geloven dat elk mens perspectief kan hervinden, mits
                iemand naast hem of haar durft te gaan staan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOE IS HET ONTSTAAN */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4 reveal">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-6">
                02 · Hoe is het ontstaan
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                Ontstaan uit{" "}
                <span className="italic text-[#E06B3F]">praktijk</span>, niet uit
                een spreadsheet.
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 reveal">
              <ol className="relative border-l border-[#1F5132]/10 pl-8 space-y-10">
                <li>
                  <span className="absolute -left-2.5 w-5 h-5 rounded-full bg-[#E06B3F]" />
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                    De observatie
                  </p>
                  <p className="font-heading text-2xl text-[#1F5132] leading-snug">
                    Trainers, welzijnswerkers en gemeenten zagen dezelfde
                    groep steeds terugkeren.
                  </p>
                  <p className="mt-3 text-[#1F5132] font-light leading-relaxed">
                    Mensen die keer op keer een traject startten, maar bij wie
                    het effect uitbleef omdat de basis ontbrak: structuur,
                    zelfbeeld en veerkracht.
                  </p>
                </li>
                <li>
                  <span className="absolute -left-2.5 w-5 h-5 rounded-full bg-[#E06B3F]" />
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                    De vraag
                  </p>
                  <p className="font-heading text-2xl text-[#1F5132] leading-snug">
                    Wat als we niet beginnen bij vacatures, maar bij de mens?
                  </p>
                  <p className="mt-3 text-[#1F5132] font-light leading-relaxed">
                    Vanuit die vraag ontstond een ervaringsgericht
                    programma, samen gebouwd met ervaren trainers en
                    lokale welzijnspartners die de doelgroep écht kennen.
                  </p>
                </li>
                <li>
                  <span className="absolute -left-2.5 w-5 h-5 rounded-full bg-[#E06B3F]" />
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                    De aanpak
                  </p>
                  <p className="font-heading text-2xl text-[#1F5132] leading-snug">
                    80% ervaring, 20% reflectie, met twee trainers en een
                    buddy per groep.
                  </p>
                  <p className="mt-3 text-[#1F5132] font-light leading-relaxed">
                    Zo werd LevelLeven geboren: geen methode van buitenaf,
                    maar een aanpak die groeit uit wat werkt in de wijk.
                  </p>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* AANSLUITING OP LANDELIJKE KADERS */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]" data-testid="over-ons-kaders">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-6">
                Aansluiting op landelijke kaders
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                In lijn met{" "}
                <span className="italic text-[#E06B3F]">
                  Participatiewet in Balans
                </span>.
              </h2>
            </div>
            <div className="md:col-span-7 reveal space-y-6 text-lg text-[#1F5132] font-light leading-relaxed">
              <p>
                LevelLeven sluit aan bij de uitgangspunten van de
                Participatiewet in Balans: vertrouwen, menselijke maat,
                maatwerk en passende ondersteuning richting maatschappelijke
                participatie en werk.
              </p>
              <p>
                De methodiek wordt doorontwikkeld met wetenschappelijke
                onderbouwing en effectevaluatie, en volgt de landelijke
                kaders van VNG en Divosa.
              </p>
              <div className="mt-8 pt-6 border-t border-[#1F5132]/10">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] mb-4">
                  Bronnen
                </p>
                <ol className="space-y-3 text-sm text-[#1F5132]">
                  <li className="flex gap-3">
                    <span className="text-[#3E4A42]">01</span>
                    <span>
                      Ministerie van Sociale Zaken en Werkgelegenheid.
                      (2024). <em>Participatiewet in Balans</em>. Den Haag:
                      Ministerie van SZW.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#3E4A42]">02</span>
                    <span>
                      Vereniging van Nederlandse Gemeenten. (2026).{" "}
                      <em>
                        Model Re-integratieverordening Participatiewet
                      </em>
                      . Den Haag: VNG.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#3E4A42]">03</span>
                    <span>
                      Divosa. (2025).{" "}
                      <em>Divosa Richtlijnen Werk & Inkomen</em>. Utrecht:
                      Divosa.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HET GROTE DOEL */}
      <section className="py-24 sm:py-32 bg-[#1F5132] text-[#FAF7F1]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-6">
                03 · Het grote doel
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight font-light">
                Een Nederland waarin niemand{" "}
                <span className="italic text-[#D19B72]">jaren stil blijft staan</span>.
              </h2>
            </div>
            <div className="md:col-span-7 reveal space-y-6 text-lg text-[#FAF7F1]/80 font-light leading-relaxed">
              <p>
                Ons grote doel is systeemverandering: de fase vóór
                re-integratie standaard onderdeel maken van hoe gemeenten
                werken met inwoners die langdurig stilstaan.
              </p>
              <p>
                Wij willen dat elk mens, ongeacht dossier, achtergrond of
                leeftijd, een echte kans krijgt om terug te komen bij
                zichzelf. En van daaruit weer bij te dragen aan de
                samenleving, op de manier die bij hem of haar past.
              </p>
              <p className="text-[#FAF7F1]">
                Meetbare beweging. Zichtbare mensen. Duurzame participatie.
              </p>
              <div className="pt-6">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1F5132] text-white rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
                >
                  <span className="text-base font-medium">
                    Praat met ons over uw gemeente
                  </span>
                  <span className="bg-[#FAF7F1] text-[#1F5132] group-hover:bg-[#1F5132] group-hover:text-[#FAF7F1] w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                    <ArrowUpRight size={20} />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ LINK */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link
            to="/faq"
            className="inline-flex items-center gap-3 text-[#E06B3F] hover:text-[#8C4A31] font-medium link-underline"
          >
            Bekijk veelgestelde vragen
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
